import React, { useState, useEffect } from 'react';
import { useJarvisContext, A } from '../../context/JarvisContext';
import TradeDetailModal from './TradeDetailModal';
import * as A_LivePrice  from '../../a-agents/trading/A_LivePrice';
import * as A_Session    from '../../a-agents/trading/A_Session';
import * as A_Statistics from '../../a-agents/trading/A_Statistics';

// ── CSS keyframes
const HOME_STYLES = `
@keyframes hudSpin1  { from{transform:rotate(0deg)}   to{transform:rotate(360deg)} }
@keyframes hudSpin2  { from{transform:rotate(0deg)}   to{transform:rotate(-360deg)} }
@keyframes hudSpin3  { from{transform:rotate(45deg)}  to{transform:rotate(405deg)} }
@keyframes hudPulse  { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:0.7;transform:scale(1.08)} }
@keyframes scanLine  { 0%{top:0} 100%{top:100%} }
@keyframes waveAnim  { 0%,100%{transform:scaleY(0.3)} 50%{transform:scaleY(1)} }
@keyframes fadeIn    { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
@keyframes dotBlink2 { 0%,100%{opacity:1} 50%{opacity:0.15} }
@keyframes cornerGlow{ 0%,100%{opacity:0.5} 50%{opacity:1} }

/* Mobile: the Hero card's 200px HUD next to the title text was the main
   cause of the overlapping/cut-off look — on a phone the row has nowhere
   near enough width for both. V5Shell's global mobile CSS already makes
   this a wrapping flex row (so it stacks instead of squeezing), this
   just tightens the padding that was eating the little width available
   and shrinks the HUD so it isn't 200px on a ~360px screen. */
@media (max-width: 860px) {
  .jh-wrap  { padding: 12px 10px !important; }
  .jh-hero  { padding: 20px 16px !important; justify-content: center !important; text-align: center; }
  .jh-hero-text { max-width: 100% !important; }
  /* transform:scale (not width/height) — HeroHud's rings are positioned
     via inset:size*constant computed from the size PROP, so resizing the
     box in CSS alone would leave them misaligned. A visual scale keeps
     every ring's relative math intact; margin pulls the now-smaller
     rendered footprint back to where a 110px box would sit. */
  .jh-hero-hud  { transform: scale(0.55); margin: 8px auto -44px; }
}
`;

// ── Large hero HUD animation
function HeroHud({ size = 200, className }) {
  const s = size;
  const ring = { position:'absolute', borderRadius:'50%' };
  return (
    <div className={className} style={{ position:'relative', width:s, height:s, flexShrink:0 }}>
      {/* Outer static */}
      <div style={{ ...ring, inset:0, border:'1px solid rgba(0,212,255,0.1)' }}/>
      {/* Ring 1 slow CW */}
      <div style={{ ...ring, inset:s*0.05, border:'1.5px solid rgba(0,212,255,0.2)',
        animation:'hudSpin1 10s linear infinite' }}>
        <div style={{ position:'absolute', top:'-4px', left:'50%', width:'7px', height:'7px',
          background:'#00d4ff', borderRadius:'50%', transform:'translateX(-50%)',
          boxShadow:'0 0 8px #00d4ff' }}/>
        <div style={{ position:'absolute', bottom:'-3px', right:'20%', width:'5px', height:'5px',
          background:'rgba(0,212,255,0.6)', borderRadius:'50%' }}/>
      </div>
      {/* Ring 2 medium CCW dashed */}
      <div style={{ ...ring, inset:s*0.15, border:'1px dashed rgba(0,212,255,0.3)',
        animation:'hudSpin2 7s linear infinite' }}>
        <div style={{ position:'absolute', top:'-3px', right:'25%', width:'5px', height:'5px',
          background:'#00d4ff', borderRadius:'50%', opacity:0.8 }}/>
      </div>
      {/* Ring 3 fast CW solid */}
      <div style={{ ...ring, inset:s*0.28, border:'2px solid rgba(0,212,255,0.45)',
        animation:'hudSpin3 4.5s linear infinite' }}>
        <div style={{ position:'absolute', top:'-4px', left:'30%', width:'6px', height:'6px',
          background:'#00d4ff', borderRadius:'50%', boxShadow:'0 0 6px #00d4ff' }}/>
      </div>
      {/* Inner ring */}
      <div style={{ ...ring, inset:s*0.4, border:'1px solid rgba(0,212,255,0.55)',
        animation:'hudSpin2 12s linear infinite' }}/>
      {/* Cross hairs */}
      <div style={{ position:'absolute', inset:s*0.08, display:'flex', alignItems:'center', justifyContent:'center' }}>
        <div style={{ position:'absolute', width:'100%', height:'1px', background:'rgba(0,212,255,0.08)' }}/>
        <div style={{ position:'absolute', height:'100%', width:'1px', background:'rgba(0,212,255,0.08)' }}/>
      </div>
      {/* Scan line */}
      <div style={{ position:'absolute', inset:s*0.08, borderRadius:'50%', overflow:'hidden', pointerEvents:'none' }}>
        <div style={{ position:'absolute', left:0, right:0, height:'1.5px',
          background:'linear-gradient(90deg, transparent, rgba(0,212,255,0.5), transparent)',
          animation:'scanLine 3s linear infinite' }}/>
      </div>
      {/* Center orb */}
      <div style={{ ...ring, inset:s*0.38,
        background:'radial-gradient(circle, #00d4ff 0%, #0055cc 55%, #001144 100%)',
        animation:'hudPulse 2.5s ease-in-out infinite',
        boxShadow:'0 0 30px #00d4ff, 0 0 60px #00d4ff44' }}/>
    </div>
  );
}

// ── Robotic corner frame card
function RoboCard({ children, style, color = '#00d4ff', cornerSize = 14, className }) {
  const c = (top, left, right, bottom) => ({
    position: 'absolute',
    width: cornerSize, height: cornerSize,
    borderTop:    top    ? `2px solid ${color}` : 'none',
    borderLeft:   left   ? `2px solid ${color}` : 'none',
    borderRight:  right  ? `2px solid ${color}` : 'none',
    borderBottom: bottom ? `2px solid ${color}` : 'none',
    opacity: 0.7,
    animation: 'cornerGlow 2s ease-in-out infinite',
  });
  return (
    <div className={className} style={{ position:'relative', ...style }}>
      <div style={{ ...c(true,true,false,false),  top:0,    left:0    }}/>
      <div style={{ ...c(true,false,true,false),  top:0,    right:0   }}/>
      <div style={{ ...c(false,true,false,true),  bottom:0, left:0    }}/>
      <div style={{ ...c(false,false,true,true),  bottom:0, right:0   }}/>
      {children}
    </div>
  );
}

// ── Sparkline
function Sparkline({ data = [], color = '#10b981', width = 110, height = 36 }) {
  if (data.length < 2) return null;
  const min = Math.min(...data), max = Math.max(...data), range = max - min || 1;
  const pts = data.map((v, i) =>
    `${(i / (data.length - 1)) * width},${height - ((v - min) / range) * height}`
  ).join(' ');
  return (
    <svg width={width} height={height} style={{ display:'block' }}>
      <defs>
        <linearGradient id="sfG" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.3"/>
          <stop offset="100%" stopColor={color} stopOpacity="0"/>
        </linearGradient>
      </defs>
      <polyline points={pts} fill="none" stroke={color} strokeWidth="2"
        style={{ filter:`drop-shadow(0 0 3px ${color})` }}/>
    </svg>
  );
}

// ── Circular progress (AI Confidence)
function CircleProgress({ value = 78, color = '#10b981', size = 88 }) {
  const r = (size - 10) / 2;
  const circ = 2 * Math.PI * r;
  const dash = (value / 100) * circ;
  return (
    <div style={{ position:'relative', width:size, height:size }}>
      <svg width={size} height={size} style={{ transform:'rotate(-90deg)' }}>
        <circle cx={size/2} cy={size/2} r={r} fill="none"
          stroke="rgba(255,255,255,0.07)" strokeWidth="9"/>
        <circle cx={size/2} cy={size/2} r={r} fill="none"
          stroke={color} strokeWidth="9"
          strokeDasharray={`${dash} ${circ}`} strokeLinecap="round"
          style={{ transition:'stroke-dasharray 1s ease', filter:`drop-shadow(0 0 6px ${color})` }}/>
      </svg>
      <div style={{ position:'absolute', inset:0, display:'flex', flexDirection:'column',
        alignItems:'center', justifyContent:'center' }}>
        <span style={{ fontSize:'22px', fontWeight:'900', color:'#fff', lineHeight:1 }}>{value}%</span>
      </div>
    </div>
  );
}

// ── Waveform
function Waveform() {
  const bars = [0.3,0.6,0.45,0.8,0.5,0.9,0.6,1,0.7,0.5,0.8,0.55,0.4,0.7,0.85,0.5,0.65,0.4,0.75,0.9];
  return (
    <div style={{ display:'flex', alignItems:'center', gap:'3px', height:'40px' }}>
      {bars.map((h, i) => (
        <div key={i} style={{
          width:'3px', borderRadius:'3px',
          background:'linear-gradient(180deg, #00d4ff, #0066cc)',
          height:`${h*100}%`,
          animation:`waveAnim ${0.7 + i * 0.08}s ease-in-out infinite`,
          animationDelay:`${i * 0.06}s`, opacity:0.75,
        }}/>
      ))}
    </div>
  );
}

// ── Status dot
function Dot({ active = true }) {
  return (
    <div style={{ width:'8px', height:'8px', borderRadius:'50%', flexShrink:0,
      background: active ? '#10b981' : '#374151',
      boxShadow: active ? '0 0 6px #10b981' : 'none',
      animation: active ? 'dotBlink2 2s ease-in-out infinite' : 'none',
    }}/>
  );
}

// ── Quick action button
function QBtn({ icon, label, onClick }) {
  const [h, setH] = useState(false);
  return (
    <button onClick={onClick}
      onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)}
      style={{
        flex:1, minWidth:'70px',
        background: h ? 'rgba(0,212,255,0.08)' : 'rgba(255,255,255,0.04)',
        border:`1px solid ${h ? 'rgba(0,212,255,0.35)' : 'rgba(255,255,255,0.08)'}`,
        borderRadius:'14px', padding:'16px 8px', cursor:'pointer',
        display:'flex', flexDirection:'column', alignItems:'center', gap:'9px',
        transition:'all 0.2s',
      }}>
      <span style={{ fontSize:'22px', animation: h ? 'iconGlow 1s ease-in-out infinite' : 'none' }}>{icon}</span>
      <span style={{ fontSize:'12px', fontWeight:'600',
        color: h ? '#00d4ff' : '#9ca3af', textAlign:'center', letterSpacing:'0.3px' }}>
        {label}
      </span>
    </button>
  );
}

const QUOTES = [
  '"Discipline today, freedom tomorrow." – JARVIS',
  '"The market rewards patience, not impulse." – JARVIS',
  '"Structure first. Entry second. Always." – JARVIS',
  '"Protect capital. The market will be here tomorrow." – JARVIS',
  '"ICT taught the tool. You become the trader." – JARVIS',
];

const PLAN_KEY = 'jarvis_today_plan';
const DEFAULT_PLAN = [
  { id:1, task:'Market Analysis',            done:false },
  { id:2, task:'Find High Probability Setups', done:false },
  { id:3, task:'Execute with Discipline',    done:false },
  { id:4, task:'Log Trades',                 done:false },
  { id:5, task:'Daily Teaching (5 min)',      done:false },
];

// ═══════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════
export default function JarvisHome() {
  const { dispatch } = useJarvisContext();
  const nav = (s) => dispatch({ type: A.SET_SCREEN, payload: s });

  const [price,   setPrice]   = useState(null);
  const [session, setSession] = useState(null);
  const [stats,   setStats]   = useState(null);
  const [trades,  setTrades]  = useState([]);
  const [plan,    setPlan]    = useState(() => {
    try { return JSON.parse(localStorage.getItem(PLAN_KEY) || 'null') || DEFAULT_PLAN; }
    catch { return DEFAULT_PLAN; }
  });
  const [modal,   setModal]   = useState(null);
  const [quote]               = useState(() => QUOTES[Math.floor(Math.random() * QUOTES.length)]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    async function load() {
      try {
        const [p, s, st] = await Promise.all([
          A_LivePrice.run(),
          A_Session.run(),
          A_Statistics.run({ period: 'ALL' }),
        ]);
        if (!alive) return;
        setPrice(p);
        setSession(s);
        setStats(st?.stats || null);
        const raw = JSON.parse(localStorage.getItem('jarvis_trades') || '[]');
        setTrades(raw.slice(-10).reverse());
      } catch {}
      setLoading(false);
    }
    load();
    const t = setInterval(() => A_LivePrice.run().then(p => alive && setPrice(p)).catch(() => {}), 30000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  const togglePlan = (id) => {
    const next = plan.map(p => p.id === id ? { ...p, done: !p.done } : p);
    setPlan(next);
    localStorage.setItem(PLAN_KEY, JSON.stringify(next));
  };

  const confidence = stats?.winRate ? Math.round(stats.winRate * 0.9) : 78;
  const confColor  = confidence >= 70 ? '#10b981' : confidence >= 50 ? '#f59e0b' : '#ef4444';
  const priceUp    = (price?.change || 0) >= 0;
  const sparkData  = price ? Array.from({ length: 22 }, (_, i) => {
    const base = price.price - price.change;
    return base + (price.change * i / 21) + (Math.random() - 0.5) * 1.5;
  }) : [];

  // Card shared style
  const card = {
    background: 'rgba(10,14,26,0.9)',
    border: '1px solid rgba(255,255,255,0.07)',
    borderRadius: '14px',
    padding: '18px',
  };

  return (
    <>
      <style>{HOME_STYLES}</style>

      <div className="jh-wrap" style={{ padding:'20px 22px', animation:'fadeIn 0.35s ease' }}>

        {/* ── HERO ──────────────────────────────────────────────────── */}
        <RoboCard color="#00d4ff" cornerSize={18} className="jh-hero" style={{
          ...card,
          background: 'linear-gradient(135deg, rgba(0,212,255,0.05) 0%, rgba(6,10,24,0.97) 100%)',
          border: '1px solid rgba(0,212,255,0.14)',
          marginBottom: '16px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          overflow: 'hidden', position: 'relative', padding: '28px 30px',
        }}>
          {/* bg glow */}
          <div style={{ position:'absolute', top:'-60px', right:'200px', width:'300px', height:'300px',
            background:'radial-gradient(circle, rgba(0,212,255,0.05) 0%, transparent 65%)',
            borderRadius:'50%', pointerEvents:'none' }}/>
          <div className="jh-hero-text" style={{ flex:1, zIndex:1 }}>
            <div style={{
              fontSize: '58px', fontWeight: '900', color: '#00d4ff',
              fontFamily: 'monospace', letterSpacing: '5px', lineHeight: 1,
              textShadow: '0 0 24px rgba(0,212,255,0.6)', marginBottom: '6px',
            }}>JARVIS</div>
            <div style={{
              fontSize: '16px', fontWeight: '800', color: '#10b981',
              letterSpacing: '3px', marginBottom: '14px',
              textShadow: '0 0 10px rgba(16,185,129,0.7)',
            }}>ONLINE &amp; READY</div>
            <div style={{ fontSize: '14px', color: 'rgba(255,255,255,0.5)', lineHeight: '1.65', maxWidth: '320px', marginBottom: '22px' }}>
              I'm your AI assistant. I analyze, learn and help you grow every single day.
            </div>
            <button onClick={() => nav('v5chat')} style={{
              padding: '12px 28px', borderRadius: '10px',
              background: 'linear-gradient(135deg, #00d4ff, #0066cc)',
              border: 'none', color: '#000', cursor: 'pointer',
              fontWeight: '800', fontSize: '14px', letterSpacing: '1px',
              boxShadow: '0 0 20px rgba(0,212,255,0.4)',
            }}>◈ Start Analysis</button>
          </div>
          <HeroHud size={200} className="jh-hero-hud" />
        </RoboCard>

        {/* ── 3 INFO CARDS ──────────────────────────────────────────── */}
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'14px', marginBottom:'16px' }}>

          {/* XAU/USD */}
          <RoboCard color={priceUp ? '#10b981' : '#ef4444'} style={{ ...card }}>
            <div style={{ display:'flex', alignItems:'center', gap:'9px', marginBottom:'12px' }}>
              <span style={{ fontSize:'22px' }}>🪙</span>
              <span style={{ fontSize:'13px', fontWeight:'700', color:'rgba(255,255,255,0.45)', letterSpacing:'1px' }}>XAU/USD</span>
            </div>
            <div style={{ fontSize:'28px', fontWeight:'900', color:'#fff', fontFamily:'monospace', letterSpacing:'1px', lineHeight:1 }}>
              {price?.price ? price.price.toLocaleString('en-US', { minimumFractionDigits:2, maximumFractionDigits:2 }) : (loading ? '...' : '—')}
            </div>
            <div style={{ fontSize:'13px', fontWeight:'700', margin:'6px 0 12px',
              color: priceUp ? '#10b981' : '#ef4444',
              textShadow: priceUp ? '0 0 8px #10b981' : '0 0 8px #ef4444' }}>
              {price && price.change != null && price.changePct != null
                ? `${priceUp ? '+' : ''}${price.change.toFixed(2)} (${price.changePct.toFixed(2)}%)`
                : '—'}
            </div>
            {/* Stale-source warning — found live 2026-08-23: Yahoo's GC=F
                feed can freeze for 1-2 DAYS while TradingView's own spot
                feed keeps moving, and Jarvis had no way to tell Sir the
                number on screen wasn't live anymore. Better to say so than
                to silently show a frozen price as current. */}
            {price?.stale && (
              <div style={{ fontSize:'10px', fontWeight:'700', color:'#f59e0b', margin:'-8px 0 10px' }}>
                ⚠️ Data {price.dataAgeMinutes >= 60 ? `${(price.dataAgeMinutes/60).toFixed(1)}h` : `${Math.round(price.dataAgeMinutes)}m`} old — source lagतोय
              </div>
            )}
            <Sparkline data={sparkData} color={priceUp ? '#10b981' : '#ef4444'} width={110} height={34}/>
          </RoboCard>

          {/* Session */}
          <RoboCard color={session?.is_active ? '#10b981' : '#374151'} style={{ ...card }}>
            <div style={{ display:'flex', alignItems:'center', gap:'9px', marginBottom:'12px' }}>
              <span style={{ fontSize:'20px', animation:'iconGlow 2s ease-in-out infinite' }}>🕐</span>
              <span style={{ fontSize:'13px', fontWeight:'700', color:'rgba(255,255,255,0.45)', letterSpacing:'1px' }}>
                {session?.label || 'SESSION'}
              </span>
            </div>
            <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'8px' }}>
              <Dot active={session?.is_active} />
              <span style={{ fontSize:'18px', fontWeight:'800', color: session?.is_active ? '#10b981' : '#6b7280',
                textShadow: session?.is_active ? '0 0 8px #10b981' : 'none' }}>
                {session?.is_active ? 'Active' : 'Closed'}
              </span>
            </div>
            <div style={{ fontSize:'13px', color:'rgba(255,255,255,0.35)', marginBottom:'12px' }}>
              {session?.utcTime || '—'}
            </div>
            <div style={{
              fontSize:'12px', padding:'6px 12px', borderRadius:'8px', display:'inline-block',
              background: session?.is_active ? 'rgba(16,185,129,0.1)' : 'rgba(255,255,255,0.04)',
              color: session?.is_active ? '#10b981' : '#6b7280',
              border: `1px solid ${session?.is_active ? 'rgba(16,185,129,0.25)' : 'rgba(255,255,255,0.06)'}`,
            }}>
              {session?.volatility || 'LOW'} volatility
            </div>
          </RoboCard>

          {/* AI Confidence */}
          <RoboCard color={confColor} style={{ ...card, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center' }}>
            <div style={{ fontSize:'12px', fontWeight:'700', color:'rgba(255,255,255,0.4)', letterSpacing:'2px', marginBottom:'14px', alignSelf:'flex-start' }}>
              AI CONFIDENCE
            </div>
            <CircleProgress value={confidence} color={confColor} size={90}/>
            <div style={{ fontSize:'14px', fontWeight:'800', marginTop:'10px', color:confColor, textShadow:`0 0 10px ${confColor}` }}>
              {confidence >= 70 ? 'Bullish' : confidence >= 50 ? 'Neutral' : 'Bearish'}
            </div>
          </RoboCard>
        </div>

        {/* ── QUICK ACTIONS ─────────────────────────────────────────── */}
        <RoboCard color="rgba(255,255,255,0.1)" style={{ ...card, marginBottom:'16px', padding:'18px 18px 16px' }}>
          <div style={{ fontSize:'15px', fontWeight:'700', color:'#e2e8f0', marginBottom:'14px' }}>Quick Actions</div>
          <div style={{ display:'flex', gap:'10px', flexWrap:'wrap' }}>
            <QBtn icon="📊" label="New Analysis"  onClick={() => nav('v5chat')} />
            <QBtn icon="⭐" label="Top Setups"    onClick={() => nav('agentnetwork')} />
            <QBtn icon="🔔" label="Price Alerts"  onClick={() => nav('calendar')} />
            <QBtn icon="➕" label="Add Trade"     onClick={() => nav('journal')} />
            <QBtn icon="📋" label="Daily Recap"   onClick={() => nav('stats')} />
          </div>
        </RoboCard>

        {/* ── TODAY'S PLAN + RECENT TRADES ──────────────────────────── */}
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'14px', marginBottom:'16px' }}>

          {/* Today's Plan */}
          <RoboCard color="rgba(0,212,255,0.2)" style={{ ...card }}>
            <div style={{ fontSize:'15px', fontWeight:'700', color:'#e2e8f0', marginBottom:'14px' }}>Today's Plan</div>
            {plan.map(item => (
              <div key={item.id} onClick={() => togglePlan(item.id)}
                style={{
                  display:'flex', alignItems:'center', gap:'12px',
                  padding:'10px 0',
                  borderBottom:'1px solid rgba(255,255,255,0.04)',
                  cursor:'pointer', opacity: item.done ? 0.45 : 1,
                  transition:'opacity 0.2s',
                }}>
                <div style={{
                  width:'20px', height:'20px', borderRadius:'50%', flexShrink:0,
                  border:`2px solid ${item.done ? '#10b981' : 'rgba(255,255,255,0.2)'}`,
                  background: item.done ? '#10b981' : 'transparent',
                  display:'flex', alignItems:'center', justifyContent:'center',
                  transition:'all 0.2s',
                }}>
                  {item.done && <span style={{ fontSize:'11px', color:'#000', fontWeight:'900' }}>✓</span>}
                </div>
                <span style={{ flex:1, fontSize:'14px', color: item.done ? '#6b7280' : '#cbd5e1',
                  textDecoration: item.done ? 'line-through' : 'none' }}>
                  {item.task}
                </span>
                <span style={{ fontSize:'12px', fontWeight:'700',
                  color: item.done ? '#10b981' : '#f59e0b' }}>
                  {item.done ? 'Done' : 'Pending'}
                </span>
              </div>
            ))}
          </RoboCard>

          {/* Recent Trades */}
          <RoboCard color="rgba(0,212,255,0.2)" style={{ ...card }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'14px' }}>
              <span style={{ fontSize:'15px', fontWeight:'700', color:'#e2e8f0' }}>Recent Trades</span>
              <span style={{ fontSize:'13px', color:'#00d4ff', cursor:'pointer', fontWeight:'600' }}
                onClick={() => nav('journal')}>View All</span>
            </div>
            {trades.length === 0 ? (
              <div style={{ color:'rgba(255,255,255,0.2)', fontSize:'14px', padding:'24px 0', textAlign:'center' }}>
                No trades yet
              </div>
            ) : trades.slice(0, 4).map((t, i) => (
              <div key={i}
                onClick={() => setModal({ ...t, verdict:t.direction, reason:t.notes||'', price:t.entry||0 })}
                style={{
                  display:'flex', alignItems:'center', justifyContent:'space-between',
                  padding:'10px 0', borderBottom:'1px solid rgba(255,255,255,0.04)',
                  cursor:'pointer',
                }}>
                <div>
                  <div style={{ fontSize:'14px', fontWeight:'700', color:'#e2e8f0' }}>
                    {t.symbol || 'XAU/USD'}
                  </div>
                  <div style={{ fontSize:'12px', color:'rgba(255,255,255,0.3)', marginTop:'2px' }}>{t.date}</div>
                </div>
                <span style={{ fontSize:'13px', fontWeight:'800',
                  color: t.direction==='BUY' ? '#10b981' : '#ef4444',
                  textShadow: t.direction==='BUY' ? '0 0 6px #10b981' : '0 0 6px #ef4444' }}>
                  {t.direction}
                </span>
                <div style={{ textAlign:'right' }}>
                  <div style={{ fontSize:'14px', fontWeight:'800',
                    color: (+t.pnl_usd||0) >= 0 ? '#10b981' : '#ef4444' }}>
                    {(+t.pnl_usd||0) >= 0 ? '+' : ''}{((+t.pnl_usd||0)).toFixed(2)} pips
                  </div>
                  <div style={{ fontSize:'11px', color:'rgba(255,255,255,0.25)', marginTop:'2px' }}>{t.date}</div>
                </div>
              </div>
            ))}
          </RoboCard>
        </div>

        {/* ── JARVIS SAYS + SYSTEM STATUS ───────────────────────────── */}
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'14px' }}>

          {/* JARVIS Says */}
          <RoboCard color="rgba(0,212,255,0.2)" style={{ ...card }}>
            <div style={{ fontSize:'15px', fontWeight:'700', color:'#e2e8f0', marginBottom:'14px' }}>JARVIS Says</div>
            <Waveform/>
            <div style={{ fontSize:'14px', color:'rgba(255,255,255,0.55)', lineHeight:'1.7',
              fontStyle:'italic', marginTop:'14px' }}>
              {quote}
            </div>
          </RoboCard>

          {/* System Status */}
          <RoboCard color="rgba(0,212,255,0.2)" style={{ ...card }}>
            <div style={{ fontSize:'15px', fontWeight:'700', color:'#e2e8f0', marginBottom:'14px' }}>System Status</div>
            {[
              { icon:'⚙️', label:'AI Engine',    status:'Online',  active:true },
              { icon:'📡', label:'Data Feed',    status:'Live',    active:price?.ok !== false },
              { icon:'🔔', label:'Notifications',status:'Active',  active:true },
              { icon:'💾', label:'Memory',       status:'Synced',  active:true },
            ].map(row => (
              <div key={row.label} style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
                padding:'10px 0', borderBottom:'1px solid rgba(255,255,255,0.04)' }}>
                <div style={{ display:'flex', alignItems:'center', gap:'11px' }}>
                  <span style={{ fontSize:'16px' }}>{row.icon}</span>
                  <span style={{ fontSize:'14px', color:'rgba(255,255,255,0.55)' }}>{row.label}</span>
                </div>
                <div style={{ display:'flex', alignItems:'center', gap:'7px' }}>
                  <Dot active={row.active}/>
                  <span style={{ fontSize:'13px', fontWeight:'700',
                    color: row.active ? '#10b981' : '#ef4444' }}>{row.status}</span>
                </div>
              </div>
            ))}
            {stats && (
              <div style={{ display:'flex', gap:'10px', marginTop:'14px' }}>
                <div style={{ flex:1, background:'rgba(16,185,129,0.08)', borderRadius:'10px', padding:'10px', textAlign:'center',
                  border:'1px solid rgba(16,185,129,0.15)' }}>
                  <div style={{ fontSize:'18px', fontWeight:'900', color:'#10b981' }}>{stats.winRate}%</div>
                  <div style={{ fontSize:'10px', color:'rgba(255,255,255,0.3)', letterSpacing:'1px', marginTop:'2px' }}>WIN RATE</div>
                </div>
                <div style={{ flex:1, background:'rgba(0,212,255,0.08)', borderRadius:'10px', padding:'10px', textAlign:'center',
                  border:'1px solid rgba(0,212,255,0.15)' }}>
                  <div style={{ fontSize:'18px', fontWeight:'900', color:'#00d4ff' }}>{stats.total || 0}</div>
                  <div style={{ fontSize:'10px', color:'rgba(255,255,255,0.3)', letterSpacing:'1px', marginTop:'2px' }}>TRADES</div>
                </div>
              </div>
            )}
          </RoboCard>
        </div>
      </div>

      {modal && <TradeDetailModal signal={modal} onClose={() => setModal(null)}/>}
    </>
  );
}
